import type {
  CloseLostRequest,
  CloseWonRequest,
  CreateLeadRequest,
  CreateOpportunityRequest,
  CreateTaskRequest,
  DisqualifyLeadRequest,
  LeadDetailResponse,
  LeadSource,
  LeadStatsResponse,
  LeadStatus,
  LeadWriteResponse,
  LogLeadActivityRequest,
  OppStageResponse,
  OppStatsResponse,
  OpportunityDetailResponse,
  OpportunityResponse,
  ReorderOppStagesRequest,
  SetOppProbabilityRequest,
  TaskActivityResponse,
  TaskCalendarResponse,
  TaskResponse,
  TaskStatsResponse,
  TaskStatus,
  UpdateLeadRequest,
  UpdateOppStageRequest,
  UpdateOpportunityRequest,
} from '@palaccount/contracts';
import { DEFAULT_PAGE_SIZE } from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/**
 * The CRM data layer (PART 10): thin TanStack Query wrappers over the typed
 * contract client — leads (CRM-01), opportunities + the ONE configured stage
 * pipeline (CRM-02), and the shared task engine (CRM-03).
 */

export const LEADS_KEY = ['crm', 'leads'] as const;
export const OPPS_KEY = ['crm', 'opportunities'] as const;
export const STAGES_KEY = ['crm', 'stages'] as const;
export const TASKS_KEY = ['crm', 'tasks'] as const;

// ── CRM-01: leads ───────────────────────────────────────────────────────────

/**
 * CRM-01's list, one page at a time (NFR-42) — every filter applied by the
 * SERVER, including `overdue`, which is derived per row against the company's
 * today and used to be a `.filter()` here over every lead in the tenant.
 */
export function useLeads(
  query: {
    q?: string;
    status?: LeadStatus;
    source?: LeadSource;
    overdue?: 'true';
  },
  page: number,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...LEADS_KEY, query, page, pageSize],
    queryFn: async () => {
      const response = await api.leads.list({ query: { ...query, page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/** The metric strip and §5d — figures about EVERY lead, never about a page. */
export function useLeadStats() {
  return useQuery({
    queryKey: [...LEADS_KEY, 'stats'],
    queryFn: async (): Promise<LeadStatsResponse> => {
      const response = await api.leads.stats();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useLead(leadId: string | undefined) {
  return useQuery({
    queryKey: [...LEADS_KEY, 'one', leadId],
    enabled: Boolean(leadId),
    queryFn: async (): Promise<LeadDetailResponse> => {
      const response = await api.leads.get({ params: { leadId: leadId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

function useLeadInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: LEADS_KEY });
    void queryClient.invalidateQueries({ queryKey: OPPS_KEY });
  };
}

export function useCreateLead() {
  const invalidate = useLeadInvalidate();
  return useMutation({
    mutationFn: async (body: CreateLeadRequest): Promise<LeadWriteResponse> => {
      const response = await api.leads.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateLead() {
  const invalidate = useLeadInvalidate();
  return useMutation({
    mutationFn: async ({
      leadId,
      body,
    }: {
      leadId: string;
      body: UpdateLeadRequest;
    }): Promise<LeadWriteResponse> => {
      const response = await api.leads.update({ params: { leadId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useAdvanceLead() {
  const invalidate = useLeadInvalidate();
  return useMutation({
    mutationFn: async (leadId: string): Promise<LeadDetailResponse> => {
      const response = await api.leads.advance({ params: { leadId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useConvertLead() {
  const invalidate = useLeadInvalidate();
  return useMutation({
    mutationFn: async (leadId: string) => {
      const response = await api.leads.convert({ params: { leadId }, body: {} });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useDisqualifyLead() {
  const invalidate = useLeadInvalidate();
  return useMutation({
    mutationFn: async ({
      leadId,
      body,
    }: {
      leadId: string;
      body: DisqualifyLeadRequest;
    }): Promise<LeadDetailResponse> => {
      const response = await api.leads.disqualify({ params: { leadId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useLogLeadActivity() {
  const invalidate = useLeadInvalidate();
  return useMutation({
    mutationFn: async ({
      leadId,
      body,
    }: {
      leadId: string;
      body: LogLeadActivityRequest;
    }): Promise<LeadDetailResponse> => {
      const response = await api.leads.logActivity({ params: { leadId }, body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

// ── CRM-02: the pipeline ────────────────────────────────────────────────────

/**
 * `includeInactive` — CRM-02's board asks for everything, because a column
 * for a switched-off stage still has to be drawn while deals sit in it
 * (BR-DATA-06: "must keep showing on old documents"). The PICKERS on that
 * same screen filter the list they were handed, which is the other half of
 * the same rule.
 */
export function useStages(includeInactive = false) {
  return useQuery({
    queryKey: [...STAGES_KEY, includeInactive],
    queryFn: async (): Promise<OppStageResponse[]> => {
      const response = await api.opportunities.listStages({
        query: includeInactive ? { includeInactive: 'true' } : {},
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.stages;
    },
  });
}

function useOppInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: OPPS_KEY });
    void queryClient.invalidateQueries({ queryKey: STAGES_KEY });
  };
}

export function useUpdateStage() {
  const invalidate = useOppInvalidate();
  return useMutation({
    mutationFn: async ({
      stageId,
      body,
    }: {
      stageId: string;
      body: UpdateOppStageRequest;
    }): Promise<OppStageResponse[]> => {
      const response = await api.opportunities.updateStage({ params: { stageId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.stages;
    },
    onSuccess: invalidate,
  });
}

/** BR-DATA-05's switch-off for §17.3.2's pipeline-stage row (crm.approve). */
export function useSetStageActive() {
  const invalidate = useOppInvalidate();
  return useMutation({
    mutationFn: async ({
      stageId,
      isActive,
    }: {
      stageId: string;
      isActive: boolean;
    }): Promise<OppStageResponse[]> => {
      const response = isActive
        ? await api.opportunities.reactivateStage({ params: { stageId }, body: {} })
        : await api.opportunities.deactivateStage({ params: { stageId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.stages;
    },
    onSuccess: invalidate,
  });
}

export function useReorderStages() {
  const invalidate = useOppInvalidate();
  return useMutation({
    mutationFn: async (body: ReorderOppStagesRequest): Promise<OppStageResponse[]> => {
      const response = await api.opportunities.reorderStages({ body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.stages;
    },
    onSuccess: invalidate,
  });
}

/**
 * CRM-02's deals, one page at a time (NFR-42) — every filter applied by the
 * SERVER, including `stalled`, which is derived per row and used to be a
 * `.filter()` here over every deal in the tenant.
 */
export function useOpportunities(
  query: {
    q?: string;
    status?: OpportunityResponse['status'];
    stageId?: string;
    stalled?: 'true';
  },
  page: number,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...OPPS_KEY, query, page, pageSize],
    queryFn: async () => {
      const response = await api.opportunities.list({ query: { ...query, page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * §5's headline, its by-stage panel and its forecast tiles — figures about
 * EVERY deal, so they come from a route about every deal rather than from the
 * footer of a page (PAGINATION.md §3 step 10). The same shape
 * `useLeadStats()` has, for the same reason.
 */
export function useOpportunityStats() {
  return useQuery({
    queryKey: [...OPPS_KEY, 'stats'],
    queryFn: async (): Promise<OppStatsResponse> => {
      const response = await api.opportunities.stats();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useOpportunity(opportunityId: string | undefined) {
  return useQuery({
    queryKey: [...OPPS_KEY, 'one', opportunityId],
    enabled: Boolean(opportunityId),
    queryFn: async (): Promise<OpportunityDetailResponse> => {
      const response = await api.opportunities.get({
        params: { opportunityId: opportunityId! },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useCreateOpportunity() {
  const invalidate = useOppInvalidate();
  return useMutation({
    mutationFn: async (body: CreateOpportunityRequest): Promise<OpportunityDetailResponse> => {
      const response = await api.opportunities.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateOpportunity() {
  const invalidate = useOppInvalidate();
  return useMutation({
    mutationFn: async ({
      opportunityId,
      body,
    }: {
      opportunityId: string;
      body: UpdateOpportunityRequest;
    }): Promise<OpportunityDetailResponse> => {
      const response = await api.opportunities.update({ params: { opportunityId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useAdvanceOpportunity() {
  const invalidate = useOppInvalidate();
  return useMutation({
    mutationFn: async (opportunityId: string): Promise<OpportunityDetailResponse> => {
      const response = await api.opportunities.advance({ params: { opportunityId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useSetOpportunityStage() {
  const invalidate = useOppInvalidate();
  return useMutation({
    mutationFn: async ({
      opportunityId,
      stageId,
    }: {
      opportunityId: string;
      stageId: string;
    }): Promise<OpportunityDetailResponse> => {
      const response = await api.opportunities.setStage({
        params: { opportunityId },
        body: { stageId },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useSetProbability() {
  const invalidate = useOppInvalidate();
  return useMutation({
    mutationFn: async ({
      opportunityId,
      body,
    }: {
      opportunityId: string;
      body: SetOppProbabilityRequest;
    }): Promise<OpportunityDetailResponse> => {
      const response = await api.opportunities.setProbability({
        params: { opportunityId },
        body,
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useCloseWon() {
  const invalidate = useOppInvalidate();
  return useMutation({
    mutationFn: async ({
      opportunityId,
      body,
    }: {
      opportunityId: string;
      body: CloseWonRequest;
    }) => {
      const response = await api.opportunities.closeWon({ params: { opportunityId }, body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useCloseLost() {
  const invalidate = useOppInvalidate();
  return useMutation({
    mutationFn: async ({
      opportunityId,
      body,
    }: {
      opportunityId: string;
      body: CloseLostRequest;
    }): Promise<OpportunityDetailResponse> => {
      const response = await api.opportunities.closeLost({ params: { opportunityId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useLogOppActivity() {
  const invalidate = useOppInvalidate();
  return useMutation({
    mutationFn: async ({
      opportunityId,
      body,
    }: {
      opportunityId: string;
      body: LogLeadActivityRequest;
    }): Promise<OpportunityDetailResponse> => {
      const response = await api.opportunities.logActivity({ params: { opportunityId }, body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

// ── CRM-03: the task engine ─────────────────────────────────────────────────

export interface TaskListFilters {
  scope?: 'mine' | 'all' | undefined;
  status?: TaskStatus | undefined;
  overdue?: boolean | undefined;
  relatedId?: string | undefined;
  /** The half-open due window — §5's four urgency groups, as four reads. */
  dueFrom?: string | undefined;
  dueTo?: string | undefined;
}

function taskQuery(filters: TaskListFilters) {
  return {
    ...(filters.scope ? { scope: filters.scope } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.overdue ? { overdue: 'true' as const } : {}),
    ...(filters.relatedId ? { relatedId: filters.relatedId } : {}),
    ...(filters.dueFrom ? { dueFrom: filters.dueFrom } : {}),
    ...(filters.dueTo ? { dueTo: filters.dueTo } : {}),
  };
}

/**
 * CRM-03's tasks, one page at a time (NFR-42) — every filter applied by the
 * SERVER, the due window included, so §5's four urgency groups are four paged
 * reads rather than four `.filter()` calls over everything.
 */
export function useTasks(
  filters: TaskListFilters = {},
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...TASKS_KEY, filters, page, pageSize],
    queryFn: async () => {
      const response = await api.tasks.list({ query: { ...taskQuery(filters), page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * §5's two badges, its four group counts, and the WINDOW BOUNDARIES the
 * counts used — so each group's list asks for exactly what was counted and a
 * heading can never disagree with the block under it.
 */
export function useTaskStats(scope?: 'mine' | 'all') {
  return useQuery({
    queryKey: [...TASKS_KEY, 'stats', scope],
    queryFn: async (): Promise<TaskStatsResponse> => {
      const response = await api.tasks.stats({ query: scope ? { scope } : {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/**
 * BR-CRM-03-18 — the weekly activity report under the Team list. The server
 * owns both of the rule's demands (completion-time counting, empty users
 * dropped) and the week window; the screen only draws what arrives.
 */
export function useTaskActivity(scope?: 'mine' | 'all') {
  return useQuery({
    queryKey: [...TASKS_KEY, 'activity', scope],
    queryFn: async (): Promise<TaskActivityResponse> => {
      const response = await api.tasks.activity({ query: scope ? { scope } : {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * §5's month grid — one row per DAY, never the month's tasks. A cell prints
 * three titles and a count, so that is what crosses the wire (31 rows).
 */
export function useTaskCalendar(from: string, to: string, scope?: 'mine' | 'all') {
  return useQuery({
    queryKey: [...TASKS_KEY, 'calendar', from, to, scope],
    queryFn: async (): Promise<TaskCalendarResponse> => {
      const response = await api.tasks.calendar({
        query: { from, to, ...(scope ? { scope } : {}) },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

function useTaskInvalidate() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: TASKS_KEY });
}

export function useCreateTask() {
  const invalidate = useTaskInvalidate();
  return useMutation({
    mutationFn: async (body: CreateTaskRequest): Promise<TaskResponse> => {
      const response = await api.tasks.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useCompleteTask() {
  const invalidate = useTaskInvalidate();
  return useMutation({
    mutationFn: async ({ taskId, outcome }: { taskId: string; outcome?: string | undefined }) => {
      const response = await api.tasks.complete({
        params: { taskId },
        body: outcome === undefined ? {} : { outcome },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useReopenTask() {
  const invalidate = useTaskInvalidate();
  return useMutation({
    mutationFn: async (taskId: string): Promise<TaskResponse> => {
      const response = await api.tasks.reopen({ params: { taskId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useCancelTask() {
  const invalidate = useTaskInvalidate();
  return useMutation({
    mutationFn: async (taskId: string): Promise<TaskResponse> => {
      const response = await api.tasks.cancel({ params: { taskId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useFollowUpTask() {
  const invalidate = useTaskInvalidate();
  return useMutation({
    mutationFn: async (taskId: string): Promise<TaskResponse> => {
      const response = await api.tasks.followUp({ params: { taskId }, body: {} });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}
